/**
 * Routes issue subscription events to the TanStack queries they invalidate. The subscriptions
 * already refresh Relay's cache; the secondary view renders from a query nothing pushed to.
 */
import {debounce} from '@github/mini-throttle'
import {issueBodyRefetchQueryKey} from '@github-ui/issue-body/IssueBody'
import {CONNECTION_MUTATION_KEY_ROOT} from '@github-ui/optimistic-connection/pendingOperations'
import type {QueryClient} from '@github-ui/react-query'

import {issueViewerSecondaryViewQueryKey} from './fetch-issue-viewer-secondary-view'
import {timelineItemsQueryKeyPrefix} from './timeline/fetch-timeline-items'

/**
 * The part of the viewer an update touches. Every secondary-view-backed scope resolves to the same
 * query today; the indirection forces each event to declare what it affects, and means splitting
 * that query into per-section ones later changes `keysForScope` alone.
 */
export type InvalidationScope = 'sub-issues' | 'metadata' | 'header' | 'body' | 'pinned-comment' | 'timeline'

/**
 * Payload fields the Relay subscriptions send; `null` when that kind of change didn't occur. This
 * layer and its two adapters below go away with the last subscription; the scope layer stays.
 */
export type IssueUpdatedPayload = {
  [K in
    | 'issueMetadataUpdated'
    | 'issueBodyUpdated'
    | 'issueTitleUpdated'
    | 'issueStateUpdated'
    | 'issueTypeUpdated'
    | 'issueReactionUpdated'
    | 'issueFieldsUpdated'
    | 'commentReactionUpdated'
    | 'commentUpdated'
    | 'pinnedCommentUpdated'
    | 'subIssuesUpdated'
    | 'subIssuesSummaryUpdated'
    | 'issueTransferStateUpdated'
    | 'parentIssueUpdated'
    | 'issueDependenciesSummaryUpdated']?: unknown
}

const SCOPE_BY_FIELD = {
  subIssuesUpdated: 'sub-issues',
  // Closing a sub-issue creates no membership row, so it emits this rather than `subIssuesUpdated`,
  // and the pill is derived from the rows' `closed` flags — so the list itself must be refetched.
  subIssuesSummaryUpdated: 'sub-issues',
  issueMetadataUpdated: 'metadata',
  issueFieldsUpdated: 'metadata',
  issueTypeUpdated: 'metadata',
  issueDependenciesSummaryUpdated: 'metadata',
  parentIssueUpdated: 'metadata',
  issueTitleUpdated: 'header',
  issueStateUpdated: 'header',
  issueBodyUpdated: 'body',
  issueTransferStateUpdated: 'header',
  // Comment traffic, but the secondary query selects `pinnedIssueComment.issueComment
  // .showSpammyBadge`. Its own scope, so splitting the query later doesn't inherit a guess.
  pinnedCommentUpdated: 'pinned-comment',
  // The secondary view holds none of this; the timeline queries only exist when it is mounted.
  commentUpdated: 'timeline',
} as const satisfies Partial<Record<keyof IssueUpdatedPayload, InvalidationScope>>

/** The scopes a payload affects, deduped. */
export function invalidationScopesFor(payload: IssueUpdatedPayload): InvalidationScope[] {
  const scopes = new Set<InvalidationScope>()
  for (const [field, scope] of Object.entries(SCOPE_BY_FIELD) as Array<
    [keyof typeof SCOPE_BY_FIELD, InvalidationScope]
  >) {
    if (payload[field] != null) scopes.add(scope)
  }
  return [...scopes]
}

type IssueCoordinates = {owner: string; repo: string; number: number}

/** Matches every mounted secondary view, whichever issue it holds. */
export const SECONDARY_VIEW_QUERY_KEY_PREFIX = ['issue-viewer', 'secondary-view'] as const

// How long to wait for a burst of announcements to finish before refetching.
export const INVALIDATION_COALESCE_MS = 250

/**
 * How long an update may be held back waiting for a write to finish. A write that never settles
 * must not hold an announcement for the life of the page; past this the refetch is abandoned, which
 * is what happened to every guarded update before.
 */
export const INVALIDATION_DEFER_LIMIT_MS = 10_000

const PREFIX_ID = JSON.stringify(SECONDARY_VIEW_QUERY_KEY_PREFIX)

/** Whether the refetch is still wanted by the time the wait elapses. */
type StillWanted = (queryClient: QueryClient) => boolean

// The refetch pending for each query key.
type CoalescedEffect = (queryClient: QueryClient, queryKey: readonly unknown[]) => void | Promise<void>

const pendingByKey = new Map<
  string,
  (queryClient: QueryClient, stillWanted: StillWanted | undefined, effect: CoalescedEffect) => void
>()

/** When each pending refetch stops waiting for the write that is blocking it. */
const abandonAfterByKey = new Map<string, number>()

/**
 * Collapses every announcement of the same change into one `invalidateQueries`.
 *
 * Nothing is dropped: each announcement restarts the wait rather than being discarded, so the
 * refetch is issued after the last announcement in the burst and therefore reflects it.
 */
function scheduleCoalescedEffect(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  stillWanted: StillWanted | undefined,
  effect: CoalescedEffect,
): void {
  const requestedId = JSON.stringify(queryKey)
  // A prefix-wide refetch already covers every key beneath it, and in a burst it is the one that
  // arrives first, so fold later specific requests into it rather than queueing a second refetch of
  // rows it is about to return anyway. (The reverse order just costs one extra refetch.)
  // Only fold true secondary-view children; sibling caches like nested sub-issues need their own refetch.
  const isSecondaryViewDescendant =
    queryKey.length > SECONDARY_VIEW_QUERY_KEY_PREFIX.length &&
    SECONDARY_VIEW_QUERY_KEY_PREFIX.every((part, index) => Object.is(queryKey[index], part))
  const foldIntoPrefix = isSecondaryViewDescendant && pendingByKey.has(PREFIX_ID)
  const id = foldIntoPrefix ? PREFIX_ID : requestedId
  const key = foldIntoPrefix ? SECONDARY_VIEW_QUERY_KEY_PREFIX : queryKey

  let schedule = pendingByKey.get(id)
  if (!schedule) {
    schedule = debounce(
      (client: QueryClient, effectWanted: StillWanted | undefined, scheduledEffect: CoalescedEffect) => {
        const wanted = !effectWanted || effectWanted(client)
        // Wait again rather than discard it. The announcement arrives once -- nothing redelivers it --
        // so an update dropped here leaves the row it announced missing until something else happens
        // on the issue, which on a quiet one can be a long time. Retrying this way keeps the deadline
        // it was given; only a fresh announcement sets a new one.
        if (!wanted && Date.now() < (abandonAfterByKey.get(id) ?? 0)) {
          schedule?.(client, effectWanted, scheduledEffect)
          return
        }
        pendingByKey.delete(id)
        abandonAfterByKey.delete(id)
        if (wanted) void scheduledEffect(client, key)
      },
      INVALIDATION_COALESCE_MS,
    )
    pendingByKey.set(id, schedule)
  }
  // Every announcement gets the full window. One arriving while an earlier one is still waiting is
  // the same case as the first, so leaving it the earlier deadline would drop it in whatever time
  // happened to remain.
  abandonAfterByKey.set(id, Date.now() + INVALIDATION_DEFER_LIMIT_MS)
  // A folded request joins the prefix-wide run, which is deliberately unguarded.
  schedule(queryClient, foldIntoPrefix ? undefined : stillWanted, effect)
}

function invalidateCoalesced(queryClient: QueryClient, queryKey: readonly unknown[], stillWanted?: StillWanted): void {
  scheduleCoalescedEffect(queryClient, queryKey, stillWanted, (client, scheduledQueryKey) =>
    client.invalidateQueries({queryKey: scheduledQueryKey}),
  )
}

/**
 * The query keys a scope invalidates. `timeline` covers *edits* to existing rows only: an appended
 * row is driven by `issueTimelineUpdated`, which fires once the row exists rather than ~3s earlier.
 *
 * `routeQueryKey` is supplied by callers whose header and sidebar render from a Data Router page
 * payload rather than the secondary view. It cannot be built here: `issues` depends on
 * `issue-viewer`, not the reverse, so the route passes its own key down.
 */
export function keysForScope(
  scope: InvalidationScope,
  {owner, repo, number}: IssueCoordinates,
  routeQueryKey?: readonly unknown[],
  /**
   * Narrows the timeline scope to the single section an appended row can land in. Only the append
   * notification may pass it: an edit names a row that can be in any section, and the prefix is
   * what reaches all of them.
   */
  timelineKeyOverride?: readonly unknown[],
  issueId?: string,
): ReadonlyArray<readonly unknown[]> {
  if (scope === 'timeline') {
    if (timelineKeyOverride) return [timelineKeyOverride]
    return [
      timelineItemsQueryKeyPrefix(owner, repo, number, 'comments'),
      timelineItemsQueryKeyPrefix(owner, repo, number, 'activity'),
    ]
  }
  if (scope === 'body') {
    return issueId ? [issueBodyRefetchQueryKey(issueId)] : []
  }
  // Title, state and transfer reach the viewed issue through the narrow header endpoint, which the
  // header subscribes to itself. Nothing here invalidates for them, so this module never names the
  // header's query key. A row further down still needs the broad refresh — see `prefixesForScope`.
  if (scope === 'header') return []
  const keys: Array<readonly unknown[]> = [issueViewerSecondaryViewQueryKey(owner, repo, number)]
  if (routeQueryKey) keys.push(routeQueryKey)
  return keys
}

/**
 * The prefixes a scope invalidates across every mounted view, for a change whose holder we can't
 * identify. `keysForScope` minus the coordinates, so the per-section split stays local to this file.
 */
export function prefixesForScope(
  scope: InvalidationScope,
  routeQueryKey?: readonly unknown[],
): ReadonlyArray<readonly unknown[]> {
  if (scope === 'timeline' || scope === 'body') return []
  const prefixes: Array<readonly unknown[]> = [SECONDARY_VIEW_QUERY_KEY_PREFIX]
  if (routeQueryKey) prefixes.push(routeQueryKey)
  return prefixes
}

/** Whether an optimistic connection operation is still in flight for `ownerId`. */
export function hasPendingConnectionOperations(queryClient: QueryClient, ownerId: string): boolean {
  return queryClient
    .getMutationCache()
    .findAll({mutationKey: [CONNECTION_MUTATION_KEY_ROOT], status: 'pending'})
    .some(mutation => mutation.options.mutationKey?.[1] === ownerId)
}

export function scheduleCanonicalTimelineUpdate({
  queryClient,
  issueId,
  coordinates,
  timelineKeyOverride,
  effect,
}: {
  queryClient: QueryClient
  issueId: string
  coordinates: IssueCoordinates
  timelineKeyOverride?: readonly unknown[]
  effect: CoalescedEffect
}): void {
  const [queryKey] = keysForScope('timeline', coordinates, undefined, timelineKeyOverride, issueId)
  if (!queryKey) return
  scheduleCoalescedEffect(queryClient, queryKey, client => !hasPendingConnectionOperations(client, issueId), effect)
}

/**
 * Invalidates the queries `scopes` affect. Skipped while the overlay has writes in flight for this
 * issue: our own write echoes back as an event, and the settle re-invalidates this key anyway.
 */
export function invalidateScopes({
  queryClient,
  scopes,
  issueId,
  coordinates,
  routeQueryKey,
  timelineKeyOverride,
}: {
  queryClient: QueryClient
  scopes: readonly InvalidationScope[]
  issueId: string
  coordinates: IssueCoordinates
  routeQueryKey?: readonly unknown[]
  timelineKeyOverride?: readonly unknown[]
}): void {
  // Distinct scopes resolve to the same query while the view is undivided, so dedupe.
  const keys = new Map<string, readonly unknown[]>()
  for (const scope of scopes) {
    for (const queryKey of keysForScope(scope, coordinates, routeQueryKey, timelineKeyOverride, issueId)) {
      keys.set(JSON.stringify(queryKey), queryKey)
    }
  }
  if (keys.size === 0) return

  const routeKeyId = routeQueryKey ? JSON.stringify(routeQueryKey) : undefined
  for (const [id, queryKey] of keys) {
    // The route query is deliberately unguarded: the guard defers until a write settles, and the
    // settle re-invalidates the secondary key afterwards -- nothing settles the route query, so
    // guarding it risks abandoning the refresh rather than delaying it. The payload carries no
    // optimistic state of its own to lose.
    if (id === routeKeyId) {
      invalidateCoalesced(queryClient, queryKey)
      continue
    }
    // Guarded inside rather than here: a write in flight defers the refetch rather than cancelling
    // it, and a write can also start during the wait.
    invalidateCoalesced(queryClient, queryKey, client => !hasPendingConnectionOperations(client, issueId))
  }
}

/**
 * Invalidates every mounted secondary view, for a change to an issue rendered as a row inside one.
 * Matching is by prefix because a row knows only its own id and rows nest arbitrarily deep.
 */
export function invalidateScopesForNested({
  queryClient,
  scopes,
  includeSecondaryView = true,
  additionalQueryKeyPrefixes = [],
  routeQueryKey,
}: {
  queryClient: QueryClient
  scopes: readonly InvalidationScope[]
  includeSecondaryView?: boolean
  additionalQueryKeyPrefixes?: ReadonlyArray<readonly unknown[]>
  routeQueryKey?: readonly unknown[]
}): void {
  const prefixes = new Map<string, readonly unknown[]>()
  // callers that only care about nested sub-issues pass includeSecondaryView: false so we skip the
  // secondary-view keys entirely. defaults to true so every existing caller keeps its old behavior
  if (includeSecondaryView) {
    for (const scope of scopes) {
      for (const queryKey of prefixesForScope(scope, routeQueryKey)) {
        prefixes.set(JSON.stringify(queryKey), queryKey)
      }
    }
  }
  for (const queryKey of additionalQueryKeyPrefixes) {
    prefixes.set(JSON.stringify(queryKey), queryKey)
  }
  if (prefixes.size === 0) return

  // No pending-operation suppression here, unlike above: the settle only invalidates the mutating
  // view's own key, so dropping this prefix-wide one-shot event would strand every other view.
  for (const queryKey of prefixes.values()) {
    invalidateCoalesced(queryClient, queryKey)
  }
}

/** Adapter for the Relay subscriptions, which carry a payload rather than a scope. */
export function invalidateForIssueUpdate({
  queryClient,
  payload,
  issueId,
  coordinates,
}: {
  queryClient: QueryClient
  payload: IssueUpdatedPayload
  issueId: string
  coordinates: IssueCoordinates
}): void {
  invalidateScopes({queryClient, scopes: invalidationScopesFor(payload), issueId, coordinates})
}

export function invalidateForNestedIssueUpdate({
  queryClient,
  payload,
  includeSecondaryView,
  additionalQueryKeyPrefixes,
}: {
  queryClient: QueryClient
  payload: IssueUpdatedPayload
  includeSecondaryView?: boolean
  additionalQueryKeyPrefixes?: ReadonlyArray<readonly unknown[]>
}): void {
  invalidateScopesForNested({
    queryClient,
    scopes: invalidationScopesFor(payload),
    includeSecondaryView,
    additionalQueryKeyPrefixes,
  })
}
